import { useContext, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import type { User } from '@esg/esg-global-types';
import { CLOUD_FUNCTION_URL } from '../../env';
import { UserContext } from '../../context/UserContext';
import { export_type_label } from '../../lib/data_export/data_export';
import UserActivityExportParameters from './parameters/UserActivityExportParameters';
import ESGDataExportParameters from './parameters/ESGDataExportParameters';
import ReadinessGaugeExportParameters from './parameters/ReadinessGaugeExportParameters';
import { GroupContext } from '../../context/GroupContext';
import { checkUserGroupAdmin } from '../../util/user_access';
import RawMeterDataUtilityManagementExportParameters from './parameters/RawMeterDataUtilityManagementExportParameters';
import RawDistributionPointDataUtilityManagementExportParameters from './parameters/RawDistributionPointDataUtilityManagementExportParameters';
import RawApplicationDataUtilityManagementExportParameters from './parameters/RawApplicationDataUtilityManagementExportParameters';
import ReportingMonthlyDataUtilityManagementExportParameters from './parameters/ReportingMonthlyDataUtilityManagementExportParameters';
import RawDailyConsumptionDataUtilityManagementExportParameters from './parameters/RawDailyConsumptionDataUtilityManagementExportParameters';
import MetricRecordActivityExportParameters from './parameters/MetricRecordActivityExportParameters';

export interface ExportType {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  displayParameters?: (export_type: ExportType) => JSX.Element;
  checkExportPermissions?: (user: User, group_id?: string) => boolean;
}

interface ExportTypeSelectProps {
  handleChangeExportType: (export_type: ExportType | null) => void;
  options?: Array<ExportType>;
  selected_options?: ExportType | null;
  disabled?: boolean;
  input_label?: string;
  clearable?: boolean;
  searchable?: boolean;
  color?: string;
  disable_underline?: boolean;
}

// Full list of potential exports to be used by other export components.
export const export_types: Array<ExportType> = [
  {
    id: 'export_esg_data',
    name: `ESG Data`,
    description: `Export esg metric record data in raw form.`,
    endpoint: `${CLOUD_FUNCTION_URL}/exportMetricRecords`,
    displayParameters: (export_type: ExportType) => (
      <ESGDataExportParameters export_type={export_type} />
    )
  },
  {
    id: 'export_readiness_gauge_responses',
    name: `Readiness Gauge Responses`,
    description: `Export completed readiness gauge responses.`,
    endpoint: `${CLOUD_FUNCTION_URL}/exportReadinessGaugeResponses`,
    displayParameters: (export_type: ExportType) => (
      <ReadinessGaugeExportParameters export_type={export_type} />
    ),
    checkExportPermissions: (user: User) => user.super_admin
  },
  {
    id: 'export_metric_record_activity',
    name: `Metric Record Activity`,
    description: `Export metric record audit log activity to a CSV file.`,
    endpoint: `${CLOUD_FUNCTION_URL}/exportMetricRecordActivity`,
    displayParameters: (export_type: ExportType) => (
      <MetricRecordActivityExportParameters export_type={export_type} />
    ),
    checkExportPermissions: (user: User, group_id: string | undefined) =>
      group_id ? checkUserGroupAdmin(user, group_id) : false
  },
  {
    id: 'export_user_module_summary',
    name: `User Activity`,
    description: `Export user activity metrics to a CSV file.`,
    endpoint: `${CLOUD_FUNCTION_URL}/exportUserModuleSummary`,
    displayParameters: (export_type: ExportType) => (
      <UserActivityExportParameters export_type={export_type} />
    ),
    checkExportPermissions: (user: User, group_id: string | undefined) =>
      group_id ? checkUserGroupAdmin(user, group_id) : false
  },
  {
    id: 'export_raw_meter_data_utility_management',
    name: `Raw Meter Data: Utility Management`,
    description: `Export raw meter data for utilities to a CSV file.`,
    endpoint: `${CLOUD_FUNCTION_URL}/exportRawMeterDataUtilityManagement`,
    displayParameters: (export_type: ExportType) => (
      <RawMeterDataUtilityManagementExportParameters export_type={export_type} />
    )
  },
  {
    id: 'export_raw_application_data_utility_management',
    name: `Raw Application Data: Utility Management`,
    description: `Export raw appliction data for utilities to a CSV file.`,
    endpoint: `${CLOUD_FUNCTION_URL}/exportRawApplicationDataUtilityManagement`,
    displayParameters: (export_type: ExportType) => (
      <RawApplicationDataUtilityManagementExportParameters export_type={export_type} />
    )
  },
  {
    id: 'export_raw_distribution_point_data_utility_management',
    name: `Raw Distribution Point Data: Utility Management`,
    description: `Export raw distribution point data for utilities to a CSV file.`,
    endpoint: `${CLOUD_FUNCTION_URL}/exportRawDistributionPointDataUtilityManagement`,
    displayParameters: (export_type: ExportType) => (
      <RawDistributionPointDataUtilityManagementExportParameters export_type={export_type} />
    )
  },
  {
    id: 'export_raw_daily_consumption_data_utility_management',
    name: `Raw Daily Consumption Data: Utility Management`,
    description: `Export raw daily consumption data for utilities to a CSV file.`,
    endpoint: `${CLOUD_FUNCTION_URL}/exportRawDailyConsumptionDataUtilityManagement`,
    displayParameters: (export_type: ExportType) => (
      <RawDailyConsumptionDataUtilityManagementExportParameters export_type={export_type} />
    )
  },
  {
    id: 'export_reporting_monthly_data_utility_management',
    name: `Reporting Monthly Data: Utility Management`,
    description: `Export reporting monthly data for utilities to a CSV file.`,
    endpoint: `${CLOUD_FUNCTION_URL}/exportReportingMonthlyDataUtilityManagement`,
    displayParameters: (export_type: ExportType) => (
      <ReportingMonthlyDataUtilityManagementExportParameters export_type={export_type} />
    )
  }
];

/**
 * Export Type selection auto-complete component that handles a variety of built-in scenarios.
 * @param {ExportType | null} props.handleChangeExportType Callback function to notify the parent component when the selected options have changed.
 * @param {Array<ExportType>} props.options Custom list of options provided by the parent component.
 * @param {ExportType | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {boolean} props.searchable Allows the user to type to search in the autocomplete. Defaults to true.
 * @param {string} props.color A custom color used for the input component of the autocomplete. Defaults to standard MUI color.
 * @param {string} props.disable_underline Allows the hiding of the underline on a standard variant text field.
 * @returns {JSX.Element}
 */
const ExportTypeSelect = (props: ExportTypeSelectProps) => {
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);

  if (!group || !user) return null;

  const [selectedOption, setSelectedOption] = useState<ExportType | null>(null);

  // Define selected options
  useEffect(() => {
    setSelectedOption(props.selected_options ? props.selected_options : null);
  }, [props.options, props.selected_options]);

  const allowed_export_types: Array<ExportType> = export_types.filter((export_type: ExportType) =>
    export_type.checkExportPermissions ? export_type.checkExportPermissions(user, group.id) : true
  );

  return (
    <Autocomplete
      id="export-type-select"
      fullWidth
      disabled={props.disabled}
      disableClearable={props.clearable === false}
      options={props.options ?? allowed_export_types}
      value={selectedOption}
      getOptionLabel={(option: ExportType) => {
        return option.name;
      }}
      isOptionEqualToValue={(option: ExportType, value: ExportType) => option.id === value.id}
      noOptionsText={`No ${export_type_label.many.toLowerCase()}`}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={props.input_label ?? export_type_label.one}
          variant="standard"
          inputProps={{ ...params.inputProps, readOnly: props.searchable === false }}
          InputProps={{ ...params.InputProps, disableUnderline: props.disable_underline }}
          sx={{
            input: { color: props.color ? props.color : 'currentcolor' },
            '& .MuiSvgIcon-root': {
              color: props.color ? props.color : 'currentcolor'
            }
          }}
        />
      )}
      onChange={(event: React.SyntheticEvent<Element, Event>, value: ExportType | null) => {
        setSelectedOption(value);
        props.handleChangeExportType(value);
      }}
    />
  );
};

export default ExportTypeSelect;
